import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, AlertTriangle, CheckCircle2, Mail } from "lucide-react";
import { track, attribution, identifyUser } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: 1,
    question: "Does your boss frequently take credit for your work or ideas?",
    trait: "narcissism",
    weight: 1
  },
  {
    id: 2,
    question: "Does your boss often manipulate situations to get what they want, even if it means being dishonest?",
    trait: "machiavellianism",
    weight: 1
  },
  {
    id: 3,
    question: "Does your boss show little remorse when their actions negatively affect others?",
    trait: "psychopathy",
    weight: 1
  },
  {
    id: 4,
    question: "Does your boss constantly seek admiration and validation from others?",
    trait: "narcissism",
    weight: 1
  },
  {
    id: 5,
    question: "Does your boss use flattery or charm strategically to achieve their goals?",
    trait: "machiavellianism",
    weight: 1
  },
  {
    id: 6,
    question: "Does your boss seem callous or insensitive to the feelings of team members?",
    trait: "psychopathy",
    weight: 1
  },
  {
    id: 7,
    question: "Does your boss expect special treatment or act as though rules don't apply to them?",
    trait: "narcissism",
    weight: 1
  }
];

const OPTIONS = [
  { value: "1", label: "Never", score: 1 },
  { value: "2", label: "Rarely", score: 2 },
  { value: "3", label: "Sometimes", score: 3 },
  { value: "4", label: "Often", score: 4 },
  { value: "5", label: "Always", score: 5 },
];

const WarningSignsQuiz = ({ onClose, onBookSession }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [quizId, setQuizId] = useState(null);
  const [email, setEmail] = useState("");
  const [emailState, setEmailState] = useState("idle");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    track("quiz_started", { questions: QUESTIONS.length });
  }, []);

  // Auto-advance when answer is selected
  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQuestion]: parseInt(value) };
    setAnswers(newAnswers);
    track("quiz_question_answered", { question: currentQuestion + 1, answer: parseInt(value) });
    
    // Small delay for visual feedback before advancing
    setIsTransitioning(true);
    setTimeout(() => {
      if (currentQuestion < QUESTIONS.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Last question - show results and save
        saveQuizResults(newAnswers);
        setShowResults(true);
      }
      setIsTransitioning(false);
    }, 400);
  };

  const saveQuizResults = async (finalAnswers) => {
    try {
      const results = calculateResults(finalAnswers);
      track("quiz_completed", {
        level: results.level,
        overall: Number(results.overall.toFixed(2)),
        narcissism: Number(results.narcissism.toFixed(2)),
        machiavellianism: Number(results.machiavellianism.toFixed(2)),
        psychopathy: Number(results.psychopathy.toFixed(2)),
      });
      const response = await axios.post(`${API}/quiz-results`, {
        answers: finalAnswers,
        scores: results,
        completed_at: new Date().toISOString(),
        attribution: attribution()
      });
      setQuizId(response.data.id);
    } catch (error) {
      console.error("Error saving quiz results:", error);
      // Don't block user experience if save fails
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailError("");
    setEmailState("sending");
    try {
      await axios.post(`${API}/quiz-results/${quizId}/email`, {
        email: email.trim(),
        origin_url: window.location.origin,
      });
      identifyUser({ email: email.trim().toLowerCase() });
      track("quiz_email_captured", { level: calculateResults().level });
      setEmailState("sent");
    } catch (error) {
      setEmailError("We couldn't send that just now. Check the address and try again.");
      setEmailState("idle");
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResults = (answersToUse = answers) => {
    const traits = {
      narcissism: { score: 0, count: 0 },
      machiavellianism: { score: 0, count: 0 },
      psychopathy: { score: 0, count: 0 }
    };

    QUESTIONS.forEach((q, idx) => {
      if (answersToUse[idx]) {
        traits[q.trait].score += answersToUse[idx];
        traits[q.trait].count += 1;
      }
    });

    // Calculate averages and overall
    const narcAvg = traits.narcissism.count > 0 ? traits.narcissism.score / traits.narcissism.count : 0;
    const machAvg = traits.machiavellianism.count > 0 ? traits.machiavellianism.score / traits.machiavellianism.count : 0;
    const psychAvg = traits.psychopathy.count > 0 ? traits.psychopathy.score / traits.psychopathy.count : 0;
    const overallAvg = (narcAvg + machAvg + psychAvg) / 3;

    return {
      narcissism: narcAvg,
      machiavellianism: machAvg,
      psychopathy: psychAvg,
      overall: overallAvg,
      level: overallAvg >= 4 ? "high" : overallAvg >= 3 ? "moderate" : overallAvg >= 2 ? "low" : "minimal"
    };
  };

  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

  const ScoreBar = ({ label, score, description, color }) => {
    const percentage = (score / 5) * 100;
    return (
      <div className="mb-6">
        <div className="flex justify-between mb-2">
          <span className="text-white font-medium">{label}</span>
          <span className="text-slate-400 text-sm">{score.toFixed(1)}/5</span>
        </div>
        <div className="h-3 bg-slate-800 rounded-full overflow-hidden mb-2">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
        <p className="text-slate-500 text-xs">{description}</p>
      </div>
    );
  };

  if (showResults) {
    const results = calculateResults();
    
    return (
      <div className="p-6">
        <div className="text-center mb-8">
          {results.level === "high" ? (
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          ) : results.level === "moderate" ? (
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          ) : (
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          )}
          <h3 className="font-serif text-2xl text-white mb-2">Your Results</h3>
          <p className="text-slate-400">
            {results.level === "high" && "Your responses indicate significant warning signs of Dark Triad traits."}
            {results.level === "moderate" && "Your responses suggest some concerning patterns worth addressing."}
            {results.level === "low" && "Your responses show mild indicators. Some patterns may benefit from attention."}
            {results.level === "minimal" && "Your responses suggest minimal Dark Triad indicators."}
          </p>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-6 mb-8">
          <h4 className="text-sm uppercase tracking-wider text-slate-400 mb-6">Trait Breakdown</h4>
          
          <ScoreBar 
            label="Narcissism" 
            score={results.narcissism} 
            color="#F59E0B"
            description="Grandiosity, need for admiration, sense of entitlement"
          />
          <ScoreBar 
            label="Machiavellianism" 
            score={results.machiavellianism} 
            color="#8B5CF6"
            description="Manipulation, deceit, strategic exploitation"
          />
          <ScoreBar 
            label="Psychopathy" 
            score={results.psychopathy} 
            color="#EF4444"
            description="Callousness, lack of remorse, insensitivity"
          />
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-6 mb-8">
          <h4 className="font-serif text-lg text-white mb-3">What This Means</h4>
          <p className="text-slate-400 text-sm leading-relaxed mb-4">
            {results.level === "high" && 
              "These results suggest your boss may exhibit significant Dark Triad traits. This can create a challenging work environment and impact your wellbeing. Professional guidance can help you develop effective coping strategies."}
            {results.level === "moderate" && 
              "These results indicate your boss shows some Dark Triad characteristics. Understanding these patterns can help you navigate the relationship more effectively."}
            {results.level === "low" && 
              "While the indicators are relatively low, any Dark Triad traits in leadership can still impact team dynamics. Awareness is the first step to managing the situation."}
            {results.level === "minimal" && 
              "Your boss appears to show minimal Dark Triad traits based on your responses. However, if you're experiencing workplace difficulties, other factors may be at play."}
          </p>
          <p className="text-slate-500 text-xs italic">
            Note: This quiz is for educational purposes only and is not a clinical diagnosis.
          </p>
        </div>

        {emailState === "sent" ? (
          <div
            className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 p-6 mb-8 flex items-start gap-4"
            data-testid="quiz-email-success"
          >
            <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-serif text-lg text-white mb-1">On its way</h4>
              <p className="text-slate-400 text-sm leading-relaxed">
                Your full breakdown is heading to {email.trim()}. If it isn't there in a few minutes, check spam.
              </p>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submitEmail}
            className="bg-slate-900/50 border border-[#D4AF37]/30 p-6 mb-8"
            data-testid="quiz-email-capture"
          >
            <div className="flex items-start gap-3 mb-3">
              <Mail className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
              <div>
                <h4 className="font-serif text-lg text-white leading-snug">Send me my full breakdown</h4>
                <p className="text-slate-400 text-sm leading-relaxed mt-1">
                  Get this profile by email, with what each score means and what actually works on this type of boss.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="quiz-email-input"
                className="flex-1 bg-slate-950 border border-slate-700 px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
              />
              <Button
                type="submit"
                disabled={emailState === "sending" || !quizId}
                data-testid="quiz-email-submit"
                className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none px-6 py-3 h-auto uppercase tracking-widest text-xs disabled:opacity-60"
              >
                {emailState === "sending" ? "Sending…" : "Email my results"}
              </Button>
            </div>
            {emailError && (
              <p className="text-red-400 text-xs mt-3" data-testid="quiz-email-error">
                {emailError}
              </p>
            )}
            <p className="text-slate-600 text-xs mt-3">One email with your results. No newsletter, no sharing.</p>
          </form>
        )}

        <div className="space-y-4" data-testid="quiz-result-ctas">
          <div className="bg-[#D4AF37]/[0.06] border border-[#D4AF37]/30 p-5">
            <p className="font-serif text-lg text-white mb-1">Your next step</p>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              {results.level === "high" || results.level === "moderate"
                ? "Start handling this week to week. Premium unlocks the full client portal — log every incident, watch the pattern build, and get counter-strategies matched to this exact profile."
                : "Keep an eye on it. Premium unlocks the full client portal — log incidents, track the pattern over time, and get counter-strategies if things escalate."}
            </p>
            <Link
              to="/portal/register?checkout=premium_monthly"
              onClick={() => track("quiz_result_cta_clicked", { cta: "go_premium", level: results.level })}
              data-testid="quiz-result-premium-btn"
              className="flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium py-4 uppercase tracking-widest text-xs transition-colors"
            >
              Start with Premium — €19/mo
            </Link>
            <p className="text-slate-600 text-xs mt-3 text-center">Cancel anytime · or €149/year</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => {
                track("quiz_result_cta_clicked", { cta: "book_consultation", level: results.level });
                onBookSession();
              }}
              data-testid="quiz-result-book-btn"
              className="flex-1 bg-transparent border border-slate-700 text-slate-300 hover:border-[#D4AF37] hover:text-[#D4AF37] hover:bg-transparent rounded-none py-5 uppercase tracking-widest text-xs"
            >
              Prefer 1-on-1? Book a consultation
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                track("quiz_result_cta_clicked", { cta: "close", level: results.level });
                onClose();
              }}
              data-testid="quiz-result-close-btn"
              className="sm:w-auto text-slate-500 hover:text-slate-300 hover:bg-transparent rounded-none py-5 uppercase tracking-widest text-xs"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-slate-400 mb-2">
          <span>Question {currentQuestion + 1} of {QUESTIONS.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2 bg-slate-800" />
      </div>

      {/* Question */}
      <div className={`mb-8 transition-opacity duration-300 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
        <h3 className="text-xl text-white mb-6 leading-relaxed">
          {QUESTIONS[currentQuestion].question}
        </h3>

        <RadioGroup
          value={answers[currentQuestion]?.toString() || ""}
          onValueChange={handleAnswer}
          className="space-y-3"
          disabled={isTransitioning}
        >
          {OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center">
              <RadioGroupItem
                value={option.value}
                id={`option-${option.value}`}
                className="peer sr-only"
                disabled={isTransitioning}
              />
              <Label
                htmlFor={`option-${option.value}`}
                className={`flex-1 p-4 border border-slate-700 cursor-pointer hover:border-[#D4AF37] peer-data-[state=checked]:bg-[#D4AF37] peer-data-[state=checked]:text-slate-950 peer-data-[state=checked]:border-[#D4AF37] transition-all text-slate-300 ${isTransitioning ? 'pointer-events-none' : ''}`}
              >
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        
        <p className="text-slate-500 text-xs mt-4 text-center">
          Select an option to continue
        </p>
      </div>

      {/* Back button only */}
      <div className="flex justify-start">
        <Button
          variant="ghost"
          onClick={prevQuestion}
          disabled={currentQuestion === 0 || isTransitioning}
          className="text-slate-400 hover:text-white hover:bg-slate-800"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>
    </div>
  );
};

export default WarningSignsQuiz;
